import type { TimelineProject, TimelineClip } from "@scroll/shared-types/timeline";

export const TIMELINE_DRAFT_SCHEMA_VERSION = 1;
export const TIMELINE_DRAFT_DB_NAME = "scroll-timeline-drafts";
export const TIMELINE_DRAFT_DB_VERSION = 1;
export const TIMELINE_DRAFT_STORE = "drafts";
export const TIMELINE_DRAFT_MEDIA_STORE = "draftMedia";
export const DRAFT_MEDIA_URI_PREFIX = "draft://media/";

export interface TimelineDraftRecord {
  contextKey: string;
  userId: string;
  projectJson: TimelineProject;
  mediaIds: string[];
  schemaVersion: number;
  createdAt: number;
  updatedAt: number;
}

export interface TimelineDraftMedia {
  mediaId: string;
  contextKey: string;
  blob: Blob;
  mimeType: string;
  originalName: string | null;
  createdAt: number;
}

export interface TimelineDraftStore {
  putDraft(record: TimelineDraftRecord): Promise<void>;
  getDraft(contextKey: string): Promise<TimelineDraftRecord | null>;
  deleteDraft(contextKey: string): Promise<void>;
  /** Return every draft authored by the given user, in no defined order. */
  listDraftsByUser(userId: string): Promise<TimelineDraftRecord[]>;
  putMedia(media: TimelineDraftMedia): Promise<void>;
  getMedia(mediaId: string): Promise<TimelineDraftMedia | null>;
  deleteMediaForContext(contextKey: string): Promise<void>;
}

export interface LocalBlobMap {
  [sourceUri: string]: { blob: Blob; originalName?: string };
}

export interface SaveDraftArgs {
  contextKey: string;
  userId: string;
  project: TimelineProject;
  localBlobs?: LocalBlobMap;
  now?: () => number;
}

export interface RestoredDraft {
  project: TimelineProject;
  /** Object URLs created during restore. The caller must revoke them when the draft is no longer needed. */
  objectUrls: string[];
  /** Synthesized File for the primary clip, reconstructed from the stored
   *  Blob. The upload modal can set this as `selectedFile` so autosave keeps
   *  snapshotting the same primary blob on subsequent edits. Null when the
   *  primary clip's source isn't a stored draft blob (e.g. external blob URL). */
  primaryFile: File | null;
}

/** Lightweight summary used by the drafts-list UI. Avoids loading blob payloads. */
export interface DraftSummary {
  contextKey: string;
  createdAt: number;
  updatedAt: number;
  /** Optional project name set by the editor's "Untitled" / rename flow. */
  projectName?: string;
  clipCount: number;
  aspectRatio: TimelineProject["aspectRatio"];
  /** Sum of trimmed clip durations in seconds. Lossy for transitions, good enough for the card. */
  totalDuration: number;
}

export class TimelineDraftQuotaError extends Error {
  constructor(message = "Browser storage quota exceeded") {
    super(message);
    this.name = "TimelineDraftQuotaError";
  }
}

function generateMediaId(): string {
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function isQuotaError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { name?: string; code?: number };
  return e.name === "QuotaExceededError" || e.code === 22;
}

function rewriteClipSource(clip: TimelineClip, mapping: Record<string, string>): TimelineClip {
  const mapped = mapping[clip.sourceUri];
  return mapped ? { ...clip, sourceUri: mapped } : clip;
}

function rewriteProjectSources(
  project: TimelineProject,
  mapping: Record<string, string>,
): TimelineProject {
  if (Object.keys(mapping).length === 0) return project;
  return {
    ...project,
    clips: project.clips.map((clip) => rewriteClipSource(clip, mapping)),
  };
}

export class TimelineDraftService {
  constructor(
    private readonly store: TimelineDraftStore,
    private readonly createMediaId: () => string = generateMediaId,
  ) {}

  async saveDraft(args: SaveDraftArgs): Promise<void> {
    const now = args.now?.() ?? Date.now();
    const localBlobs = args.localBlobs ?? {};
    const existing = await this.store.getDraft(args.contextKey);

    // Drop any media owned by the previous draft snapshot to keep IndexedDB clean.
    if (existing) {
      await this.store.deleteMediaForContext(args.contextKey);
    }

    // Only persist Blobs that the project actually references. Anything else
    // would be an orphan media record (e.g. an old object URL the editor no
    // longer points at).
    const projectSourceUris = new Set(args.project.clips.map((c) => c.sourceUri));
    const sourceUriToMediaUri: Record<string, string> = {};
    const mediaIds: string[] = [];

    for (const [sourceUri, { blob, originalName }] of Object.entries(localBlobs)) {
      if (!projectSourceUris.has(sourceUri)) continue;
      const mediaId = this.createMediaId();
      const mediaUri = `${DRAFT_MEDIA_URI_PREFIX}${mediaId}`;
      sourceUriToMediaUri[sourceUri] = mediaUri;
      mediaIds.push(mediaId);

      try {
        await this.store.putMedia({
          mediaId,
          contextKey: args.contextKey,
          blob,
          mimeType: blob.type || "application/octet-stream",
          originalName: originalName ?? null,
          createdAt: now,
        });
      } catch (err) {
        if (isQuotaError(err)) throw new TimelineDraftQuotaError();
        throw err;
      }
    }

    const projectJson = rewriteProjectSources(args.project, sourceUriToMediaUri);

    try {
      await this.store.putDraft({
        contextKey: args.contextKey,
        userId: args.userId,
        projectJson,
        mediaIds,
        schemaVersion: TIMELINE_DRAFT_SCHEMA_VERSION,
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
      });
    } catch (err) {
      if (isQuotaError(err)) throw new TimelineDraftQuotaError();
      throw err;
    }
  }

  /** Cheap existence + ownership check. Does not read blobs or create object URLs. */
  async hasDraft(contextKey: string, userId: string): Promise<boolean> {
    const record = await this.store.getDraft(contextKey);
    if (!record) return false;
    if (record.userId !== userId) return false;
    if (record.schemaVersion !== TIMELINE_DRAFT_SCHEMA_VERSION) return false;
    return true;
  }

  async restoreDraft(contextKey: string, userId: string): Promise<RestoredDraft | null> {
    const record = await this.store.getDraft(contextKey);
    if (!record) return null;
    if (record.userId !== userId) return null;
    if (record.schemaVersion !== TIMELINE_DRAFT_SCHEMA_VERSION) {
      // Future migrations would slot in here. For now refuse incompatible records.
      return null;
    }

    const objectUrls: string[] = [];
    const mediaUriToObjectUrl: Record<string, string> = {};
    const mediaIdToRecord: Record<string, TimelineDraftMedia> = {};

    for (const mediaId of record.mediaIds) {
      const media = await this.store.getMedia(mediaId);
      if (!media) continue;
      const objectUrl = URL.createObjectURL(media.blob);
      objectUrls.push(objectUrl);
      mediaUriToObjectUrl[`${DRAFT_MEDIA_URI_PREFIX}${mediaId}`] = objectUrl;
      mediaIdToRecord[mediaId] = media;
    }

    const project: TimelineProject = {
      ...record.projectJson,
      clips: record.projectJson.clips.map((clip) => {
        const restored = mediaUriToObjectUrl[clip.sourceUri];
        return restored ? { ...clip, sourceUri: restored } : clip;
      }),
    };

    // Reconstruct a File for the primary clip so the upload modal can keep
    // autosave wired without forcing the user to re-pick the source. The
    // primary is whichever clip references a draft://media URI — clips[0]
    // most of the time, but reordering can move it. Added clips uploaded
    // to Vercel Blob carry blobKey/blobUrl and an external sourceUri, not
    // a draft://media URI, so the search uniquely identifies the local
    // primary blob.
    let primaryFile: File | null = null;
    for (const clip of record.projectJson.clips) {
      const uri = clip.sourceUri ?? "";
      if (!uri.startsWith(DRAFT_MEDIA_URI_PREFIX)) continue;
      const mediaId = uri.slice(DRAFT_MEDIA_URI_PREFIX.length);
      const media = mediaIdToRecord[mediaId];
      if (!media) continue;
      primaryFile = new File(
        [media.blob],
        media.originalName ?? "draft-video.mp4",
        { type: media.mimeType || "video/mp4" },
      );
      break;
    }

    return { project, objectUrls, primaryFile };
  }

  async listDrafts(userId: string): Promise<DraftSummary[]> {
    const records = await this.store.listDraftsByUser(userId);
    return records
      .filter((r) => r.schemaVersion === TIMELINE_DRAFT_SCHEMA_VERSION)
      .map<DraftSummary>((r) => {
        const clips = r.projectJson.clips ?? [];
        const totalDuration = clips.reduce((sum, c) => {
          const trimmed = (c.trimEnd ?? 0) - (c.trimStart ?? 0);
          if (Number.isFinite(trimmed) && trimmed > 0) return sum + trimmed / (c.speed || 1);
          if (c.type === "image" && Number.isFinite(c.displayDuration)) return sum + c.displayDuration;
          return sum;
        }, 0);
        return {
          contextKey: r.contextKey,
          createdAt: r.createdAt,
          updatedAt: r.updatedAt,
          projectName: r.projectJson.name,
          clipCount: clips.length,
          aspectRatio: r.projectJson.aspectRatio,
          totalDuration,
        };
      })
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }

  async discardDraft(contextKey: string): Promise<void> {
    await this.store.deleteMediaForContext(contextKey);
    await this.store.deleteDraft(contextKey);
  }
}

/** Build a stable compose-context key so a refreshed draft is only offered for the same source.
 *
 * Prefers a SHA-256 content hash. Falls back to user id + filename + size + lastModified when
 * crypto.subtle is unavailable (e.g. some test envs). The fallback has small collision risk if
 * the user has two videos with identical name/size/mtime; for autosave that's an acceptable tradeoff.
 */
export async function buildComposeContextKey(args: {
  userId: string;
  sourceFile: File;
}): Promise<string> {
  const { userId, sourceFile } = args;
  try {
    const subtle = globalThis.crypto?.subtle;
    if (subtle && typeof sourceFile.arrayBuffer === "function") {
      const buffer = await sourceFile.arrayBuffer();
      const digest = await subtle.digest("SHA-256", buffer);
      const hash = Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      return `${userId}:${hash}`;
    }
  } catch {
    // Fall through to deterministic metadata fallback.
  }
  return `${userId}:${sourceFile.name}:${sourceFile.size}:${sourceFile.lastModified}`;
}

/** In-memory store used by tests and as a fallback when IndexedDB isn't usable. */
export function createInMemoryDraftStore(): TimelineDraftStore {
  const drafts = new Map<string, TimelineDraftRecord>();
  const media = new Map<string, TimelineDraftMedia>();

  return {
    async putDraft(record) {
      drafts.set(record.contextKey, record);
    },
    async getDraft(contextKey) {
      return drafts.get(contextKey) ?? null;
    },
    async deleteDraft(contextKey) {
      drafts.delete(contextKey);
    },
    async listDraftsByUser(userId) {
      const out: TimelineDraftRecord[] = [];
      for (const record of drafts.values()) {
        if (record.userId === userId) out.push(record);
      }
      return out;
    },
    async putMedia(m) {
      media.set(m.mediaId, m);
    },
    async getMedia(mediaId) {
      return media.get(mediaId) ?? null;
    },
    async deleteMediaForContext(contextKey) {
      for (const [id, m] of media) {
        if (m.contextKey === contextKey) media.delete(id);
      }
    },
  };
}

/** Open the IndexedDB database used by the production store. */
function openTimelineDraftDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(TIMELINE_DRAFT_DB_NAME, TIMELINE_DRAFT_DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error("Failed to open timeline draft db"));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(TIMELINE_DRAFT_STORE)) {
        db.createObjectStore(TIMELINE_DRAFT_STORE, { keyPath: "contextKey" });
      }
      if (!db.objectStoreNames.contains(TIMELINE_DRAFT_MEDIA_STORE)) {
        const mediaStore = db.createObjectStore(TIMELINE_DRAFT_MEDIA_STORE, { keyPath: "mediaId" });
        mediaStore.createIndex("byContextKey", "contextKey", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

function promisifyRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });
}

function promisifyTransaction(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB transaction failed"));
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
  });
}

/** Production IndexedDB-backed store. Returns null if IndexedDB isn't available. */
export function createIndexedDbDraftStore(): TimelineDraftStore | null {
  if (typeof indexedDB === "undefined") return null;

  let dbPromise: Promise<IDBDatabase> | null = null;
  const getDb = () => {
    if (!dbPromise) dbPromise = openTimelineDraftDb();
    return dbPromise;
  };

  return {
    async putDraft(record) {
      const db = await getDb();
      const tx = db.transaction(TIMELINE_DRAFT_STORE, "readwrite");
      tx.objectStore(TIMELINE_DRAFT_STORE).put(record);
      await promisifyTransaction(tx);
    },
    async getDraft(contextKey) {
      const db = await getDb();
      const tx = db.transaction(TIMELINE_DRAFT_STORE, "readonly");
      const result = await promisifyRequest(
        tx.objectStore(TIMELINE_DRAFT_STORE).get(contextKey),
      );
      return (result as TimelineDraftRecord | undefined) ?? null;
    },
    async deleteDraft(contextKey) {
      const db = await getDb();
      const tx = db.transaction(TIMELINE_DRAFT_STORE, "readwrite");
      tx.objectStore(TIMELINE_DRAFT_STORE).delete(contextKey);
      await promisifyTransaction(tx);
    },
    async listDraftsByUser(userId) {
      const db = await getDb();
      const tx = db.transaction(TIMELINE_DRAFT_STORE, "readonly");
      const all = (await promisifyRequest(tx.objectStore(TIMELINE_DRAFT_STORE).getAll())) as TimelineDraftRecord[];
      return all.filter((r) => r.userId === userId);
    },
    async putMedia(media) {
      const db = await getDb();
      const tx = db.transaction(TIMELINE_DRAFT_MEDIA_STORE, "readwrite");
      tx.objectStore(TIMELINE_DRAFT_MEDIA_STORE).put(media);
      await promisifyTransaction(tx);
    },
    async getMedia(mediaId) {
      const db = await getDb();
      const tx = db.transaction(TIMELINE_DRAFT_MEDIA_STORE, "readonly");
      const result = await promisifyRequest(
        tx.objectStore(TIMELINE_DRAFT_MEDIA_STORE).get(mediaId),
      );
      return (result as TimelineDraftMedia | undefined) ?? null;
    },
    async deleteMediaForContext(contextKey) {
      const db = await getDb();
      const tx = db.transaction(TIMELINE_DRAFT_MEDIA_STORE, "readwrite");
      const store = tx.objectStore(TIMELINE_DRAFT_MEDIA_STORE);
      const index = store.index("byContextKey");
      const keys = await promisifyRequest(index.getAllKeys(contextKey));
      for (const key of keys as IDBValidKey[]) {
        store.delete(key);
      }
      await promisifyTransaction(tx);
    },
  };
}

let cachedService: TimelineDraftService | null = null;

/** Default singleton wired to IndexedDB. Falls back to an in-memory store when IndexedDB is unavailable
 * (e.g. private-mode Safari without storage, or jsdom). */
export function getTimelineDraftService(): TimelineDraftService {
  if (cachedService) return cachedService;
  const store = createIndexedDbDraftStore() ?? createInMemoryDraftStore();
  cachedService = new TimelineDraftService(store);
  return cachedService;
}

/** Test-only override for the singleton. */
export function __setTimelineDraftServiceForTesting(service: TimelineDraftService | null): void {
  cachedService = service;
}
